import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {FieldError} from '@/components/field-error'
import {PerfilLineaExtras} from '@/components/perfil-linea-extras'
import type {PerfilLineaRead, PerfilLineaWrite} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {tope} from '@/lib/campos-texto'
import {validateRequired} from '@/lib/validation'
import {
  CONFIRMADAS_AL_ENTRAR,
  EXTRAS_VACIOS,
  extrasDeLinea,
  POR_LOTE,
  renglonesDe,
  type Apertura,
  type Bloque,
  type ExtrasCrudos,
} from '@/lib/perfil-bloques'

/** KAI-144, KAI-146. En la carga por lote la vista previa es la confirmación: cada fila entra
 * como una línea confirmada. Los riesgos van en `perfil-riesgo-dialog` (KAI-334). */
export function PerfilLineaDialog(props: {
  apertura: Apertura | null
  pending: boolean
  error: string | null
  onCerrar: () => void
  onAgregar: (lineas: PerfilLineaWrite[]) => void
  onGuardar: (linea: PerfilLineaRead, cambios: PerfilLineaWrite) => void
}) {
  // Un formulario nuevo por apertura (`key`): el estado nace de lo que se abre, sin efecto.
  if (!props.apertura) return null
  const clave = props.apertura.modo === 'editar' ? props.apertura.linea.clave : `nueva:${props.apertura.bloque}`
  return <FormularioDeLinea key={clave} {...props} apertura={props.apertura} />
}

function extrasDe(apertura: Apertura): ExtrasCrudos {
  if (apertura.modo === 'nueva') return EXTRAS_VACIOS
  const {linea} = apertura
  return {
    tipo: (linea.subtipo as ExtrasCrudos['tipo']) || 'tecnico',
    tipoCompetencia: (linea.subtipo as ExtrasCrudos['tipoCompetencia']) || 'tecnica',
  }
}

function bloqueDe(apertura: Apertura): Bloque {
  return apertura.modo === 'nueva' ? apertura.bloque : (apertura.linea.bloque as Bloque)
}

function rotuloDe(bloque: Bloque, lote: boolean): string {
  if (lote) return `perfilCargo.bloque.${bloque}`
  return `perfilCargo.tabla.item.${bloque}`
}

function FormularioDeLinea({
  apertura,
  pending,
  error,
  onCerrar,
  onAgregar,
  onGuardar,
}: {
  apertura: Apertura
  pending: boolean
  error: string | null
  onCerrar: () => void
  onAgregar: (lineas: PerfilLineaWrite[]) => void
  onGuardar: (linea: PerfilLineaRead, cambios: PerfilLineaWrite) => void
}) {
  const {t} = useTranslation()
  const bloque = bloqueDe(apertura)
  const nueva = apertura.modo === 'nueva'
  const lote = nueva && POR_LOTE.has(bloque)
  const [texto, setTexto] = useState(apertura.modo === 'editar' ? apertura.linea.texto : '')
  const [extras, setExtras] = useState<ExtrasCrudos>(() => extrasDe(apertura))
  const [errorTexto, setErrorTexto] = useState<string | undefined>()
  // Las filas se derivan del campo, no son un segundo estado: editarlas reescribe el campo.
  const filas = lote ? renglonesDe(texto) : []

  function guardar(event: FormEvent) {
    event.preventDefault()
    const hallazgo = validateRequired(lote ? filas.join('\n') : texto)
    setErrorTexto(hallazgo ? t(hallazgo) : undefined)
    if (hallazgo) return
    const nuevos = extrasDeLinea(bloque, extras)
    if (apertura.modo === 'editar') {
      const {linea} = apertura
      onGuardar(linea, {
        clave: linea.clave,
        bloque: linea.bloque,
        texto: texto.trim(),
        confirmado: linea.confirmado || CONFIRMADAS_AL_ENTRAR.has(bloque),
        ...nuevos,
      })
      return
    }
    if (lote) {
      onAgregar(filas.map((fila) => ({clave: null, bloque, texto: fila, confirmado: true, ...nuevos})))
      return
    }
    onAgregar([{clave: null, bloque, texto: texto.trim(), confirmado: CONFIRMADAS_AL_ENTRAR.has(bloque), ...nuevos}])
  }

  function reescribir(nuevas: string[]) {
    setTexto(nuevas.join('\n'))
    setErrorTexto(undefined)
  }

  // Un solo juego de props para los dos controles; el `maxLength` va en la etiqueta y no en
  // el spread (gate R10).
  const campoTexto = {
    id: 'perfil-linea-texto',
    'data-testid': nueva ? 'perfil-nuevo-texto' : 'perfil-editar-texto',
    value: texto,
    onBlur: (event: {target: {value: string}}) => {
      const hallazgo = validateRequired(event.target.value)
      setErrorTexto(hallazgo ? t(hallazgo) : undefined)
    },
    onChange: (event: {target: {value: string}}) => {
      setTexto(event.target.value)
      setErrorTexto(undefined)
    },
  }
  // Responsabilidades es un párrafo (KAI-142); los bloques por lote también al editar, porque
  // un `<input>` borra los saltos de línea (KAI-243).
  const parrafo = POR_LOTE.has(bloque) || bloque === 'responsabilidades'
  const que = t(`perfilCargo.conteo.${bloque}`, {count: filas.length})

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="max-w-lg" data-testid={nueva ? 'perfil-agregar-linea' : 'perfil-editar-linea'}>
        <DialogHeader>
          <DialogTitle>{t(nueva ? 'perfilCargo.detalle.agregarLinea' : 'perfilCargo.tabla.editar')}</DialogTitle>
        </DialogHeader>
        <form className="space-y-3" noValidate onSubmit={guardar}>
          <div className="space-y-2">
            <Label htmlFor="perfil-linea-texto">{t(rotuloDe(bloque, lote))}</Label>
            {parrafo ? (
              <Textarea maxLength={tope('TEXTO2000')} {...campoTexto} />
            ) : (
              <Input maxLength={tope('TEXTO2000')} {...campoTexto} />
            )}
            <FieldError error={errorTexto} />
          </div>
          {lote && filas.length > 0 && (
            <ul className="space-y-1" data-testid="perfil-lote">
              {filas.map((fila, i) => (
                <li key={i} className="flex items-center gap-1">
                  <Input
                    maxLength={tope('TEXTO2000')}
                    aria-label={que}
                    data-testid={`perfil-lote-fila-${i}`}
                    value={fila}
                    onChange={(event) => reescribir(filas.map((otra, j) => (j === i ? event.target.value : otra)))}
                  />
                  {i > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid={`perfil-lote-unir-${i}`}
                      onClick={() =>
                        reescribir([...filas.slice(0, i - 1), `${filas[i - 1]} ${fila}`, ...filas.slice(i + 1)])
                      }>
                      {t('perfilCargo.lote.unir')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    data-testid={`perfil-lote-quitar-${i}`}
                    onClick={() => reescribir(filas.filter((_, j) => j !== i))}>
                    {t('perfilCargo.lote.quitar')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <PerfilLineaExtras bloque={bloque} valor={extras} onChange={setExtras} />
          {error && (
            <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="perfil-linea-error">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onCerrar}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={pending || (lote && filas.length === 0)}
              data-testid="perfil-linea-guardar">
              {lote
                ? t('perfilCargo.lote.agregar', {count: filas.length, que})
                : t(nueva ? 'perfilCargo.detalle.agregarLinea' : 'perfilCargo.tabla.guardar')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
